import React, { useEffect, useState, useMemo } from 'react';
import { subscribeCategories, createCategory, updateCategory, deleteCategory } from '../services/categories';
import AdminLayout from '../components/AdminLayout';
import { Icon } from '@iconify/react';
import Modal from '../components/Modal';

function formatDate(v) {
  if (!v) return '—';
  try {
    const date =
      v?.toMillis ? new Date(v.toMillis()) :
      typeof v === 'number' ? new Date(v) :
      typeof v === 'string' ? new Date(v) : null;

    if (!date || isNaN(date.getTime())) return String(v);

    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#6c5ce7');
  const [colorText, setColorText] = useState('#6c5ce7');
  const [iconId, setIconId] = useState('');
  const [premium, setPremium] = useState(false);

  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [fallbackActive, setFallbackActive] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  const pushToast = ({ type = 'info', title, description }) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, title, description }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  };
  const dismissToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  // Icon search state
  const [iconSearchTerm, setIconSearchTerm] = useState('');
  const [iconResults, setIconResults] = useState([]);
  const [iconLoading, setIconLoading] = useState(false);
  const [iconError, setIconError] = useState('');


  useEffect(() => {
    const unsub = subscribeCategories(
      (list) => setCategories(list),
      (err) => {
        setError(err?.message || 'Realtime connection failed; using periodic updates.');
        setFallbackActive(true);
        pushToast({ type: 'error', title: 'Realtime listener issue', description: err?.message || 'Falling back to polling every 10s.' });
      }
    );
    return () => unsub();
  }, []);

  // Debounced icon search via Iconify API
  useEffect(() => {
    if (!iconSearchTerm.trim()) {
      setIconResults([]);
      setIconError('');
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setIconLoading(true);
        setIconError('');
        const url = `https://api.iconify.design/search?query=${encodeURIComponent(iconSearchTerm)}&limit=40`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error('Icon search failed');
        const data = await res.json();
        const items = (data?.icons || []).map((it) => {
          if (typeof it === 'string') {
            const [prefix, name] = it.split(':');
            return { id: it, prefix, name };
          }
          return { id: `${it.prefix}:${it.name}`, prefix: it.prefix, name: it.name };
        });
        setIconResults(items);
      } catch (e) {
        if (e.name !== 'AbortError') setIconError(e.message || 'Failed to search icons');
      } finally {
        setIconLoading(false);
      }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [iconSearchTerm]);

  const filteredIconResults = iconResults;

  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => {
      const name = (c.name || '').toLowerCase();
      const desc = (c.description || '').toLowerCase();
      const id = (c.id || '').toLowerCase();
      const icon = (c.icon || '').toLowerCase();
      const type = c.premium ? 'premium' : 'free';
      return name.includes(q) || desc.includes(q) || id.includes(q) || icon.includes(q) || type.includes(q);
    });
  }, [categories, search]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    const getVal = (c) => {
      switch (sortKey) {
        case 'name':
          return (c.name || '').toLowerCase();
        case 'description':
          return (c.description || '').toLowerCase();
        case 'color':
          return (c.color || '').toLowerCase();
        case 'icon':
          return (c.icon || '').toLowerCase();
        case 'type':
          return c.premium ? 1 : 0;
        case 'createdAt':
        default: {
          const v = c.createdAt;
          return v?.toMillis ? v.toMillis() : (typeof v === 'number' ? v : (typeof v === 'string' ? Date.parse(v) || 0 : 0));
        }
      }
    };
    list.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const hexValid = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color);

  const onColorPickerChange = (e) => {
    setColor(e.target.value);
    setColorText(e.target.value);
  };
  const onColorTextChange = (e) => {
    const v = e.target.value;
    setColorText(v);
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)) {
      setColor(v);
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setName(cat.name || '');
    const c = cat.color || '#6c5ce7';
    setColor(c);
    setColorText(c);
    setIconId(cat.icon || '');
    setDescription(cat.description || '');
    setPremium(!!cat.premium);
    setIsModalOpen(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setName('');
    setColor('#6c5ce7');
    setColorText('#6c5ce7');
    setIconId('');
    setDescription('');
    setPremium(false);
    setIsModalOpen(false);
  };

  const handleDelete = async (id) => {
    const ok = window.confirm('Delete this category? This cannot be undone.');
    if (!ok) return;
    setDeletingId(id);
    try {
      await deleteCategory(id);
      pushToast({ type: 'success', title: 'Category deleted', description: `id=${id}` });
      if (editingId === id) {
        cancelEdit();
      }
    } catch (err) {
      pushToast({ type: 'error', title: 'Failed to delete category', description: err.message || 'Unknown error' });
    } finally {
      setDeletingId(null);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');

    if (!name.trim()) {
      setStatus('idle');
      pushToast({ type: 'error', title: 'Name required', description: 'Please enter a name.' });
      return;
    }
    if (!description.trim()) {
      setStatus('idle');
      pushToast({ type: 'error', title: 'Description required', description: 'Please enter a description.' });
      return;
    }
    if (!hexValid) {
      setStatus('idle');
      pushToast({ type: 'error', title: 'Invalid color', description: 'Please enter a valid hex color (e.g., #ff9900).' });
      return;
    }
    if (!iconId) {
      setStatus('idle');
      pushToast({ type: 'error', title: 'Icon required', description: 'Please select an icon.' });
      return;
    }

    try {
      if (editingId) {
        await updateCategory(editingId, { name: name.trim(), color, icon: iconId, description: description.trim(), premium });
        pushToast({ type: 'success', title: 'Category updated', description: `id=${editingId}` });
        cancelEdit();
      } else {
        const docRef = await createCategory({ name: name.trim(), color, icon: iconId, description: description.trim(), premium });
        pushToast({ type: 'success', title: 'Category created', description: `id=${docRef.id}` });
        setName('');
        setColor('#6c5ce7');
        setColorText('#6c5ce7');
        setIconId('');
        setDescription('');
        setPremium(false);
        setIsModalOpen(false);
      }
    } catch (err) {
      pushToast({ type: 'error', title: editingId ? 'Failed to update category' : 'Failed to add category', description: err.message || 'Unknown error' });
    } finally {
      setStatus('idle');
    }
  };


  return (
    <AdminLayout>
      <div className="content-card">
        <h2 className="section-title">Category Management</h2>
        <p className="section-subtitle">Create and view categories with color and icon.</p>
        {fallbackActive && (
          <p style={{ color: '#8e44ad', marginTop: 4 }}>
            Realtime connection unavailable; updates every 10s. Check network, VPN, or extensions.
          </p>
        )}
        {editingId && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <span style={{ fontWeight: 600 }}>Editing category</span>
            <code style={{ background: 'rgba(0,0,0,0.06)', padding: '2px 6px', borderRadius: 8 }}>{editingId}</code>
            <button type="button" className="btn btn-pill btn-accent" onClick={cancelEdit}>Cancel</button>
          </div>
        )}
        {/* Unified toolbar: search + Add Category */}
        <Modal open={isModalOpen} title={editingId ? 'Edit Category' : 'Add Category'} onClose={() => setIsModalOpen(false)}
          footer={(
            <>
              <button type="button" className="btn btn-pill btn-accent" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="btn btn-pill btn-dark" type="submit" form="categoryForm" disabled={status === 'loading'}>
                {status === 'loading' ? 'Saving…' : (editingId ? 'Save Changes' : 'Add Category')}
              </button>
            </>
          )}
        >
          <form id="categoryForm" onSubmit={handleAdd} className="form-row full" style={{ maxWidth: 760 }}>
            <input
              data-autofocus
              className="input"
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <textarea
              className="input textarea"
              placeholder="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={400}
              required
            />

            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 6, alignItems: 'center' }}>
              <input
                type="color"
                aria-label="Color picker"
                value={color}
                onChange={onColorPickerChange}
                style={{ width: 42, height: 42, padding: 0, border: '1.5px solid rgba(119,104,157,0.35)', borderRadius: 8, background: 'transparent' }}
              />
              <input
                className="input"
                type="text"
                placeholder="#RRGGBB or #RGB"
                value={colorText}
                onChange={onColorTextChange}
              />
            </div>
            {!hexValid && (
              <p style={{ color: '#c0392b', marginTop: -4 }}>Invalid hex color. Example: #ff9900</p>
            )}

            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                <input
                  className="input"
                  type="text"
                  placeholder="Search icons (e.g., meditation, home, user)"
                  value={iconSearchTerm}
                  onChange={(e) => setIconSearchTerm(e.target.value)}
                />
              </div>
              {iconError && <p style={{ color: '#c0392b' }}>{iconError}</p>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 80px)', gap: 8, alignItems: 'start' }}>
                {iconLoading && <p>Searching icons…</p>}
                {!iconLoading && filteredIconResults.map((ico) => (
                  <button
                    key={ico.id}
                    type="button"
                    onClick={() => setIconId(ico.id)}
                    className="btn"
                    style={{
                      display: 'grid',
                      placeItems: 'center',
                      padding: 8,
                      borderRadius: 12,
                      border: ico.id === iconId ? '2px solid var(--accent)' : '1px solid rgba(0,0,0,0.12)',
                      background: 'rgba(255,255,255,0.8)'
                    }}
                    title={`${ico.prefix}:${ico.name}`}
                  >
                    <img src={`https://api.iconify.design/${ico.id}.svg?height=28&color=%23324654`} alt={`${ico.id}`} width={28} height={28} />
                    <div style={{ fontSize: 11, color: '#556', marginTop: 4, textAlign: 'center' }}>{ico.prefix}</div>
                  </button>
                ))}
              </div>
              {iconId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ fontWeight: 600 }}>Selected:</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img src={`https://api.iconify.design/${iconId}.svg?height=24&color=%23324654`} alt={`${iconId}`} width={24} height={24} />
                    <code style={{ background: 'rgba(0,0,0,0.06)', padding: '2px 6px', borderRadius: 8 }}>{iconId}</code>
                  </div>
                </div>
              )}
              {/* Premium / Free option */}
              <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                <label style={{ fontWeight: 600 }}>Type</label>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="radio"
                      name="categoryType"
                      checked={!premium}
                      onChange={() => setPremium(false)}
                    />
                    Free
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="radio"
                      name="categoryType"
                      checked={premium}
                      onChange={() => setPremium(true)}
                    />
                    Premium
                  </label>
                </div>
              </div>
            </div>
          </form>
        </Modal>

        <div style={{ marginTop: 16 }}>
          <div className="table-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <input
              className="input"
              type="text"
              placeholder="Search by name, description, icon, or type"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: 280, maxWidth: 360, flex: '0 1 360px' }}
            />
            <button className="btn btn-pill btn-dark" type="button" onClick={() => { setEditingId(null); setIsModalOpen(true); }}>Add Category</button>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>
                  <button type="button" onClick={() => toggleSort('name')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Name {sortKey === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort('description')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Description {sortKey === 'description' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort('color')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Color {sortKey === 'color' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort('icon')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Icon {sortKey === 'icon' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort('type')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Type {sortKey === 'type' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort('createdAt')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Created {sortKey === 'createdAt' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>
                    {c.description ? (
                      <span style={{ display: 'inline-block', maxWidth: 360, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' }}>{c.description}</span>
                    ) : (
                      '—'
                    )}
                  </td>

                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-block', width: 16, height: 16, borderRadius: 4, background: c.color || '#ccc', border: '1px solid rgba(0,0,0,0.12)' }} />
                      <span style={{ fontFamily: 'monospace' }}>{c.color || '—'}</span>
                    </div>
                  </td>
                  <td>
                    {c.icon ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Icon icon={c.icon} width="20" height="20" />
                        <code style={{ background: 'rgba(0,0,0,0.06)', padding: '2px 6px', borderRadius: 8 }}>{c.icon}</code>
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{c.premium ? 'Premium' : 'Free'}</td>
                  <td>{formatDate(c.createdAt)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-pill btn-dark" type="button" onClick={() => startEdit(c)}>Edit</button>
                      <button className="btn btn-pill btn-accent" type="button" onClick={() => handleDelete(c.id)} disabled={deletingId === c.id}>
                        {deletingId === c.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Toasts overlay */}
      <div style={{ position: 'fixed', top: 16, right: 16, display: 'grid', gap: 8, zIndex: 1000 }} aria-live="polite" aria-atomic="true">
        {toasts.map((t) => {
          const color = t.type === 'success' ? '#27ae60' : t.type === 'error' ? '#c0392b' : '#6c5ce7';
          const icon = t.type === 'success' ? 'ph:check-circle' : t.type === 'error' ? 'ph:x-circle' : 'ph:info';
          return (
            <div key={t.id} role={t.type === 'error' ? 'alert' : 'status'}
                 style={{ background: 'rgba(255,255,255,0.92)', border: '1px solid rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}`,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', backdropFilter: 'blur(6px)', borderRadius: 12,
                          padding: '10px 12px', display: 'grid', gridTemplateColumns: '24px 1fr auto', alignItems: 'center', gap: 10 }}>
              <Icon icon={icon} width="20" height="20" color={color} />
              <div>
                <div style={{ fontWeight: 600 }}>{t.title}</div>
                {t.description && <div style={{ fontSize: 13, color: '#556' }}>{t.description}</div>}
              </div>
              <button onClick={() => dismissToast(t.id)} aria-label="Dismiss"
                      style={{ border: 'none', background: 'transparent', color: '#667', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
            </div>
          );
        })}
      </div>
    </AdminLayout>
  );
}